import { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Box,
  ColorPicker,
  Divider,
  Label,
  Scrollbar,
  SpinnerLoader,
  TextFieldWithLabel,
} from 'shared/ui'
import { useBlockerAndUnload } from 'shared/ui/Blocker'
import { useReviewViewContext } from 'pages/reviews/context'
import { Preview } from 'pages/reviews/ui/Preview/Preview'
import { ReviewViewStep } from 'pages/reviews/ui/ReviewViewStep'
import { ReviewViewContentHeader } from 'pages/reviews/ui/ReviewViewContentHeader/ReviewViewContentHeader'
import { ReviewAccounts } from 'pages/reviews/ui/ReviewAccounts'
import { ImageUploader } from 'pages/reviews/ui/ImageUploader'
import styles from './styles.module.scss'

export const ReviewViewContent = observer(() => {
  const appearanceRef = useRef<HTMLDivElement | null>(null)
  const starsHighRef = useRef<HTMLDivElement | null>(null)
  const starsLowRef = useRef<HTMLDivElement | null>(null)
  const {
    activeTab,
    setActiveTab,
    scrollToStep,
    setScrollToStep,
    title,
    setTitle,
    subtitle,
    setSubtitle,
    happySubtitle,
    setHappySubtitle,
    unhappySubtitle,
    setUnhappySubtitle,
    bgColor,
    setBgColor,
    isLoading,
    imageStore,
    accountsStore,
    blockerStore,
    errorsMap,
    removeError,
  } = useReviewViewContext()

  useBlockerAndUnload(blockerStore)

  useEffect(() => {
    if (!scrollToStep) return

    let ref: HTMLDivElement | null = null

    switch (activeTab) {
      case 'happy':
        ref = starsHighRef.current
        break
      case 'unhappy':
        ref = starsLowRef.current
        break
      default:
        ref = appearanceRef.current
    }

    if (ref) {
      const viewContainer: HTMLElement | null = ref.closest('[class*=scrollbarView]')

      if (viewContainer) {
        viewContainer.scrollTo({
          top:
            viewContainer.scrollTop +
            ref.getBoundingClientRect().top -
            viewContainer.getBoundingClientRect().top,
          behavior: 'smooth',
        })
      }
    }

    setScrollToStep(null)
  }, [scrollToStep])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <SpinnerLoader ariaLabel={'ReviewViewContent'} />
      </div>
    )
  }

  return (
    <>
      <ReviewViewContentHeader />
      <div className={styles.wrap}>
        <div className={styles.left}>
          <Scrollbar>
            <div className={styles.content}>
              <div ref={appearanceRef}>
                <ReviewViewStep
                  blockId='1'
                  title='Appearance'
                  description='Set the first screen your contacts see.'
                />
              </div>
              <ImageUploader
                label='Image'
                subtitle={'We support PNGs, JPEGs under 5 MB'}
                store={imageStore}
                error={errorsMap.get('image_url') ?? ''}
              />
              <TextFieldWithLabel
                labelProps={{
                  label: {
                    text: 'Title',
                    textColor: 'var(--content-primary-tertiary)',
                    textClassName: styles.reviewsLabel,
                  },
                }}
                textFieldProps={{
                  value: title,
                  onChange: setTitle,
                  onFocus: () => setActiveTab('appearance'),
                  InputProps: {
                    placeholder: 'Enter title',
                  },
                }}
                limit={50}
                errorMessage={errorsMap.get('title')}
              />
              <TextFieldWithLabel
                labelProps={{
                  label: {
                    text: 'Subtitle',
                    textColor: 'var(--content-primary-tertiary)',
                    textClassName: styles.reviewsLabel,
                  },
                }}
                textFieldProps={{
                  value: subtitle,
                  onChange: setSubtitle,
                  onFocus: () => setActiveTab('appearance'),
                  InputProps: {
                    placeholder: 'Enter subtitle',
                  },
                }}
                limit={120}
                errorMessage={errorsMap.get('subtitle')}
              />
              <Box
                maxWidth={220}
                style={{ position: 'relative' }}
                onClick={() => setActiveTab('appearance')}
              >
                <Label
                  label={{
                    text: 'Background color',
                    textColor: 'var(--content-primary-tertiary)',
                    textClassName: styles.reviewsLabel,
                  }}
                  marginBottom={4}
                />
                <div onClick={() => setActiveTab('appearance')}>
                  <ColorPicker color={bgColor} setColor={setBgColor} isAutocorrectEnabled={false} />
                </div>
              </Box>
              <Divider
                sx={{
                  margin: '19px 0 18px',
                  borderColor: 'var(--border-neutral-default)',
                }}
              />
              <div ref={starsHighRef}>
                <ReviewViewStep
                  blockId='2'
                  title='Stars (4-5)'
                  description='Direct happy contacts to leave a public review.'
                />
              </div>
              <Box paddingTop={'8px'} onClick={() => setActiveTab('happy')}>
                <ReviewAccounts
                  store={accountsStore}
                  error={errorsMap.get('accounts') ?? ''}
                  removeError={removeError}
                />
              </Box>
              <TextFieldWithLabel
                labelProps={{
                  label: {
                    text: 'Subtitle',
                    textColor: 'var(--content-primary-tertiary)',
                    textClassName: styles.reviewsLabel,
                  },
                }}
                textFieldProps={{
                  value: happySubtitle,
                  onChange: setHappySubtitle,
                  onFocus: () => setActiveTab('happy'),
                  InputProps: {
                    placeholder: 'Enter subtitle',
                  },
                }}
                limit={120}
                errorMessage={errorsMap.get('happy_subtitle')}
              />
              <Divider
                sx={{
                  margin: '19px 0 18px',
                  borderColor: 'var(--border-neutral-default)',
                }}
              />
              <div ref={starsLowRef}>
                <ReviewViewStep
                  blockId='3'
                  title='Stars (1-3)'
                  description='Capture private feedback from unsatisfied contacts.'
                />
              </div>
              <TextFieldWithLabel
                labelProps={{
                  label: {
                    text: 'Subtitle',
                    textColor: 'var(--content-primary-tertiary)',
                    textClassName: styles.reviewsLabel,
                  },
                }}
                textFieldProps={{
                  value: unhappySubtitle,
                  onChange: setUnhappySubtitle,
                  onFocus: () => setActiveTab('unhappy'),
                  InputProps: {
                    placeholder: 'Enter subtitle',
                  },
                }}
                limit={120}
                errorMessage={errorsMap.get('unhappy_subtitle')}
              />
            </div>
          </Scrollbar>
        </div>
        <div className={styles.right}>
          <Preview />
        </div>
      </div>
    </>
  )
})
